
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Send, Paperclip, X, ClipboardList, Copy, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ENDPOINTS } from "@/config/api";

function formatTimestamp(date: Date) {
  return new Intl.DateTimeFormat("default", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

interface ChatComponentProps {
  jwt: string;
  analysisId: string;
  lang: string;
}

function ChatComponent({
  jwt,
  analysisId,
  lang
}: ChatComponentProps) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { toast } = useToast();

  // Helper function to get language base (e.g., "es" from "es-ES" or "es-MX")
  const getLanguageBase = (langCode: string) => {
    return langCode.split('-')[0];
  };

  // Debug language changes
  useEffect(() => {
    console.log("ChatComponent: Current i18n language:", i18n.language);
    console.log("ChatComponent: Lang prop:", lang);
    console.log("ChatComponent: currentLanguage state:", currentLanguage);
  }, [i18n.language, lang, currentLanguage]);

  // Update i18n language when lang prop changes
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      console.log("ChatComponent: Changing language from", i18n.language, "to", lang);
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  // Listen for language changes and force re-render
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      console.log("ChatComponent: Language changed to:", lng);
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth"
      });
    }
  }, []);

  // Fetch messages function
  const fetchMessages = async () => {
    try {
      setMessagesLoaded(false);
      const populateQuery = "chat_messages";
      const response = await fetch(`https://api.mediscribe.io/api/analyses/${analysisId}?populate=${populateQuery}`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`Error fetching messages: ${response.status}`);
      }
      const data = await response.json();
      console.log("Analysis data:", data);
      if (data.data?.attributes?.chat_messages) {
        const chatMessages = data.data.attributes.chat_messages;
        const transformedMessages = chatMessages.map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          role: msg.role,
          timestamp: new Date(msg.timestamp || Date.now())
        }));
        transformedMessages.sort((a: Message, b: Message) => a.timestamp.getTime() - b.timestamp.getTime());
        setMessages(transformedMessages);
      }
      setMessagesLoaded(true);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessagesLoaded(true);
    }
  };

  useEffect(() => {
    if (jwt && analysisId) {
      fetchMessages();
    }
  }, [jwt, analysisId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesLoaded && messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, messagesLoaded, scrollToBottom]);

  const handleCopyMessage = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast({
        title: t("copied") || "Copiado",
        description: t("message_copied") || "El mensaje ha sido copiado al portapapeles.",
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: t("error") || "Error",
        description: t("copy_error") || "No se pudo copiar el mensaje.",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const newUserMessage: Message = {
      id: "user-" + Date.now(),
      content: input,
      role: "user",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMessage]);
    const currentInput = input;
    setInput("");
    setIsLoading(true);

    // Add thinking message
    const thinkingMessage: Message = {
      id: "thinking-" + Date.now(),
      content: t("chat_thinking"),
      role: "assistant",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thinkingMessage]);

    try {
      const payload = {
        analysisId: analysisId,
        message: currentInput,
        token: jwt,
        lang: getLanguageBase(i18n.language) // Use language base instead of full code
      };
      console.log("Sending payload:", payload);
      
      const response = await fetch(ENDPOINTS.CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Response data:", data);

      // Remove thinking message and add real response
      setMessages(prev => {
        const withoutThinking = prev.filter(msg => msg.id !== thinkingMessage.id);
        return [...withoutThinking, {
          id: data.message.id || "assistant-" + Date.now(),
          content: data.message.content || "No response received",
          role: data.message.role || "assistant",
          timestamp: new Date(data.message.timestamp || Date.now())
        }];
      });
    } catch (error) {
      console.error("Error in chat:", error);

      // Remove thinking message and show error
      setMessages(prev => {
        const withoutThinking = prev.filter(msg => msg.id !== thinkingMessage.id);
        return [...withoutThinking, {
          id: "error-" + Date.now(),
          content: `Error: ${(error as Error).message || "Unknown error occurred"}`,
          role: "assistant",
          timestamp: new Date()
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateReport = (e: React.MouseEvent) => {
    e.preventDefault();
    let summaryText = "";
    const langBase = getLanguageBase(i18n.language);
    
    if (langBase === "es") {
      summaryText = "Dame un informe completo de la visita incluyendo las partes.";
    } else if (langBase === "pt") {
      summaryText = "Dê-me um relatório completo da visita, incluindo as peças.";
    } else {
      summaryText = "Give me a full report of the visit including the parts.";
    }
    
    setInput(summaryText);
    setTimeout(() => {
      if (formRef.current) {
        const submitEvent = new Event("submit", {
          bubbles: true,
          cancelable: true
        });
        formRef.current.dispatchEvent(submitEvent);
        handleSubmit({
          preventDefault: () => {}
        } as React.FormEvent);
      }
    }, 100);
  };

  const handleCreateSummary = (e: React.MouseEvent) => {
    e.preventDefault();
    let summaryText = "";
    const langBase = getLanguageBase(i18n.language);
    
    if (langBase === "es") {
      summaryText = "Haz un resumen de la visita que contenga las secciones del informe clínico ignorando aquellas partes que no tuvieron respuesta";
    } else if (langBase === "pt") {
      summaryText = "Faça um resumo da visita que contenha as seções do relatório clínico, ignorando as partes que não tiveram resposta";
    } else {
      summaryText = "Make a summary of the visit that includes the sections of the clinical report, ignoring any parts that did not have a response.";
    }
    
    setInput(summaryText);
    setTimeout(() => {
      if (formRef.current) {
        const submitEvent = new Event("submit", {
          bubbles: true,
          cancelable: true
        });
        formRef.current.dispatchEvent(submitEvent);
        handleSubmit({
          preventDefault: () => {}
        } as React.FormEvent);
      }
    }, 100);
  };

  const renderMessage = useCallback((message: Message) => {
    const isCopied = copiedMessageId === message.id;
    
    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <ReactMarkdown
              components={{
                code({ className, children, ...props }) {
                  return (
                    <code
                      {...props}
                      className={`${className} bg-gray-100 px-2 py-1 rounded text-sm font-mono`}
                    >
                      {children}
                    </code>
                  );
                },
                pre({ children }) {
                  return (
                    <pre className="bg-gray-100 p-4 rounded-md overflow-x-auto">
                      {children}
                    </pre>
                  );
                }
              }}
            >
              {message.content}
            </ReactMarkdown>
          </div>
          
          {message.role === "assistant" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleCopyMessage(message.content, message.id)}
              className="flex-shrink-0 h-8 w-8 p-0 hover:bg-gray-100"
              title={t("copy_message") || "Copiar mensaje"}
            >
              {isCopied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500" />
              )}
            </Button>
          )}
        </div>
        
        <div className={`text-xs mt-1 ${message.role === "user" ? "text-gray-600" : "text-muted-foreground"}`}>
          {formatTimestamp(message.timestamp)}
        </div>
      </div>
    );
  }, [copiedMessageId, t]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
  };

  // Debug render
  console.log("ChatComponent: Rendering with language:", i18n.language, "t('chat_title'):", t("chat_title"));

  return (
    <div className="flex flex-col h-screen w-full">
      {/* When no messages - center everything in viewport */}
      {messages.length === 0 && messagesLoaded ? (
        <div className="flex flex-col h-full justify-center items-center p-6 max-w-2xl mx-auto w-full">
          {/* Centered header */}
          <div className="flex flex-col items-center justify-center text-center mb-8">
            <div className="w-[230px] h-[100px] rounded-full flex items-center justify-center mx-auto mb-4">
              <img 
                src="/lovable-uploads/d03970cc-6452-4195-b58b-2679e58c5a1e.png" 
                alt="Clara.care logo"
                className=" rounded-full"
              />
            </div>
            <h2 className="text-base font-semibold mb-1 text-foreground">{t("chat_title")}</h2>
            <p className="text-xs text-muted-foreground">{t("chat_subtitle")}</p>
          </div>
          
          {/* Input Area - centered in viewport */}
          <div className="w-full max-w-lg">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              {selectedFile && (
                <div className="flex items-center bg-muted p-3 rounded-lg">
                  <div className="w-8 h-8 bg-muted-foreground/20 rounded flex items-center justify-center mr-3">
                    📁
                  </div>
                  <span className="text-sm text-foreground flex-grow">{selectedFile.name}</span>
                  <Button type="button" onClick={removeSelectedFile} variant="ghost" size="sm">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-between gap-3 mb-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2" 
                  type="button" 
                  onClick={handleCreateSummary} 
                  disabled={isLoading}
                >
                  <ClipboardList className="w-4 h-4" />
                  {t("chat_create_summary")}
                </Button>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2" 
                  type="button" 
                  onClick={handleCreateReport} 
                  disabled={isLoading}
                >
                  <ClipboardList className="w-4 h-4" />
                  {t("chat_create_report")}
                </Button>
              </div>
              
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t("chat_placeholder")}
                    disabled={isLoading}
                    className="min-h-[44px]"
                  />
                </div>
                
                <Input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                
                <Button
                  type="button"
                  onClick={() => document.getElementById("file-upload")?.click()}
                  disabled={true}
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                >
                  <Paperclip className="w-4 h-4 text-muted-foreground" />
                </Button>
                
                <Button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  size="icon"
                  className="flex-shrink-0"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <>
          {/* Messages Area */}
          <div className="flex-1 min-h-0">
            <ScrollArea className="h-full w-full">
              <div className="p-6 pt-32 md:pt-24 pb-[180px] sm:pb-[200px] md:pb-[220px]">
                {!messagesLoaded ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-6 pb-4">
                    {messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] px-4 py-3 rounded-lg ${
                            m.role === "user"
                              ? "bg-teal-50 text-foreground border border-teal-200"
                              : "bg-muted text-foreground"
                          }`}
                        >
                          {renderMessage(m)}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Input Area for chat with messages */}
          <div className="flex-shrink-0 bg-background p-4">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              {selectedFile && (
                <div className="flex items-center bg-muted p-3 rounded-lg">
                  <div className="w-8 h-8 bg-muted-foreground/20 rounded flex items-center justify-center mr-3">
                    📁
                  </div>
                  <span className="text-sm text-foreground flex-grow">{selectedFile.name}</span>
                  <Button type="button" onClick={removeSelectedFile} variant="ghost" size="sm">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-between gap-3 mb-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2" 
                  type="button" 
                  onClick={handleCreateSummary} 
                  disabled={isLoading}
                >
                  <ClipboardList className="w-4 h-4" />
                  {t("chat_create_summary")}
                </Button>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex items-center gap-2" 
                  type="button" 
                  onClick={handleCreateReport} 
                  disabled={isLoading}
                >
                  <ClipboardList className="w-4 h-4" />
                  {t("chat_create_report")}
                </Button>
              </div>
              
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t("chat_placeholder")}
                    disabled={isLoading}
                    className="min-h-[44px]"
                  />
                </div>
                
                <Input
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                
                <Button
                  type="button"
                  onClick={() => document.getElementById("file-upload")?.click()}
                  disabled={true}
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                >
                  <Paperclip className="w-4 h-4 text-muted-foreground" />
                </Button>
                
                <Button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  size="icon"
                  className="flex-shrink-0"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default ChatComponent;
